import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional label for logs (e.g. the screen name). */
  label?: string;
}

interface State {
  error: Error | null;
}

/**
 * App-level error boundary (#1322). A render crash anywhere under here shows a
 * recoverable screen with retry actions instead of a flat white hang — the exact
 * failure mode reported for StoreDetail after saving an order. Field reps can self-
 * recover (Try again / Back to Today / Reload) without being stranded.
 *
 * Reusable: wrap the whole app, or a single risky subtree, with <ErrorBoundary>.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string | null }) {
    console.error(`[ErrorBoundary${this.props.label ? `:${this.props.label}` : ''}] render crash`, error, info);
  }

  private reset = () => {
    // Clear the error so the same route re-renders (recovers without a full reload
    // when the crash was transient, e.g. a stale prop during a re-mount).
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-3 text-4xl">⚠️</div>
          <h2 className="mb-2 font-heading text-lg font-semibold text-slate-900">Something went wrong</h2>
          <p className="mb-5 text-sm text-slate-600">
            This screen hit an unexpected error. Your saved work is not lost. Try again, or head back to Today.
          </p>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={this.reset}
              className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/today')}
              className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Back to Today
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-xs font-medium text-slate-500 hover:text-slate-700"
            >
              Reload app
            </button>
          </div>
          <details className="mt-4 text-left">
            <summary className="cursor-pointer text-xs text-slate-400">Details for support</summary>
            <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap rounded-md bg-slate-50 p-2 text-[11px] text-slate-600 ring-1 ring-slate-100">
              {this.state.error.message}
              {this.state.error.stack && `\n\n${this.state.error.stack}`}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
